import { createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { CommonJobsStatus, FraudElement, FraudElementContributors, FraudElementObservation, GeneratedFraudElementsResponse } from "../../../api/fraudElements";
import { createFraudElements, discardGeneratedFraudElements, fetchFraudElements, generateFraudElements, getGeneratedFraudElements, updateFraudElement, upsertFraudElementObservation } from "./fraudElementsThunk";
import { RequestStatus } from "../../legalTechnicalAnalysis/Logic/assetsSlice";

export interface GeneratedFraudElementsResponseWithPartyId extends GeneratedFraudElementsResponse {
  party_id: string;
}

export const fraudElementsAdapter = createEntityAdapter<FraudElement, string>({
  selectId: (fraudElement) => fraudElement.fraud_element_id,
});

export const fraudElementObservationsAdapter = createEntityAdapter<FraudElementObservation, string>({
  selectId: (observation) => `${observation.fraud_element_id}_${observation.contributor}`,
});

export const generatedFraudElementsAdapter = createEntityAdapter<GeneratedFraudElementsResponseWithPartyId, string>({
  selectId: (generatedFraudElements) => generatedFraudElements.party_id,
});

export interface FraudElementsState {
  fraudElements: EntityState<FraudElement, string>;
  observations: EntityState<FraudElementObservation, string>;
  generatedFraudElements: EntityState<GeneratedFraudElementsResponseWithPartyId, string>;
  fraudElementIdsByPartyId: Record<string, string[]>;
  caseId?: string;
  status: RequestStatus;
  error?: string | null;
}

const initialState: FraudElementsState = {
  fraudElements: fraudElementsAdapter.getInitialState(),
  observations: fraudElementObservationsAdapter.getInitialState(),
  generatedFraudElements: generatedFraudElementsAdapter.getInitialState(),
  fraudElementIdsByPartyId: {},
  caseId: undefined,
  status: "idle",
  error: null,
};

const appendFraudElementsToPartyMap = (state: FraudElementsState, fraudElements: FraudElement[]) => {
  fraudElements.forEach(({ party_id, fraud_element_id }) => {
    if (!state.fraudElementIdsByPartyId[party_id]) {
      state.fraudElementIdsByPartyId[party_id] = [];
    }
    if (!state.fraudElementIdsByPartyId[party_id].includes(fraud_element_id)) {
      state.fraudElementIdsByPartyId[party_id].push(fraud_element_id);
    }
  });
};

const fraudElementsSlice = createSlice({
  name: "fraudElements",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFraudElements.pending, (state) => {
        state.status = "loading";
        state.error = null;
        fraudElementsAdapter.removeAll(state.fraudElements);
        fraudElementObservationsAdapter.removeAll(state.observations);
        generatedFraudElementsAdapter.removeAll(state.generatedFraudElements);
        state.fraudElementIdsByPartyId = {};
      })

      .addCase(fetchFraudElements.fulfilled, (state, action) => {
        state.status = "success";
        state.error = null;
        state.caseId = action.meta.arg;
        fraudElementsAdapter.setAll(state.fraudElements, action.payload.fraud_elements);
        fraudElementObservationsAdapter.setAll(state.observations, action.payload.observations);
        appendFraudElementsToPartyMap(state, action.payload.fraud_elements);
      })

      .addCase(fetchFraudElements.rejected, (state) => {
        state.status = "failed";
        state.error = "Failed to fetch fraud elements";
      })

      .addCase(createFraudElements.fulfilled, (state, action) => {
        fraudElementsAdapter.addMany(state.fraudElements, action.payload.fraud_elements);
        appendFraudElementsToPartyMap(state, action.payload.fraud_elements);
      })

      .addCase(updateFraudElement.fulfilled, (state, action) => {
        const { fraud_element_id, request } = action.meta.arg;
        fraudElementsAdapter.updateOne(state.fraudElements, {
          id: fraud_element_id,
          changes: {
            details: request.details,
          },
        });
      })
      
      .addCase(upsertFraudElementObservation.fulfilled, (state, action) => {
        const { fraud_element_id, request } = action.meta.arg;
        fraudElementObservationsAdapter.upsertOne(state.observations, {
          fraud_element_id,
          contributor: request.contributor,
          observation: request.observation,
        });
      })

      .addCase(generateFraudElements.fulfilled, (state, action) => {
        const { party_id } = action.meta.arg;

        generatedFraudElementsAdapter.upsertOne(state.generatedFraudElements, {
          party_id,
          fraud_elements: action.payload.fraud_elements ?? {
            status: CommonJobsStatus.SUBMITTED,
            fraud_elements: [],
          },
        });
      })
      
      .addCase(getGeneratedFraudElements.fulfilled, (state, action) => {
        const { party_id } = action.meta.arg;
        generatedFraudElementsAdapter.upsertOne(state.generatedFraudElements, {
          party_id,
          fraud_elements: action.payload.fraud_elements,
        });
      })

      .addCase(discardGeneratedFraudElements.fulfilled, (state, action) => {
        generatedFraudElementsAdapter.removeOne(state.generatedFraudElements, action.meta.arg.party_id);
      });
  },
});

export const fraudElementsReducer = fraudElementsSlice.reducer;

const fraudElementsSelectors = fraudElementsAdapter.getSelectors<RootState>(
  (state) => state.fraudElements.fraudElements
);

const fraudElementObservationSelectors = fraudElementObservationsAdapter.getSelectors<RootState>(
  (state) => state.fraudElements.observations
);

const generatedFraudElementsSelectors = generatedFraudElementsAdapter.getSelectors<RootState>(
  (state) => state.fraudElements.generatedFraudElements
);

export const selectFraudElementsStatus = (state: RootState) => state.fraudElements.status;
export const selectFraudElementsError = (state: RootState) => state.fraudElements.error;
export const selectFraudElementIds = (state: RootState) => state.fraudElements.fraudElements.ids;
export const selectFraudElementCount = (state: RootState) => state.fraudElements.fraudElements.ids.length;
export const selectFraudElementEntities = fraudElementsSelectors.selectEntities;
export const selectAllFraudElements = fraudElementsSelectors.selectAll;
export const selectFraudElementObservationEntities = fraudElementObservationSelectors.selectEntities;
export const selectAllFraudElementObservations = fraudElementObservationSelectors.selectAll;
export const selectGeneratedFraudElementEntities = generatedFraudElementsSelectors.selectEntities;
export const selectAllGeneratedFraudElements = generatedFraudElementsSelectors.selectAll;

export const selectFraudElementById = (state: RootState, fraudElementId: string) =>
  fraudElementsSelectors.selectById(state, fraudElementId);

export const selectGeneratedFraudElementsForParty = (state: RootState, partyId: string) =>
  generatedFraudElementsSelectors.selectById(state, partyId)?.fraud_elements;

export const selectFraudElementObservation = (
  state: RootState,
  fraudElementId: string,
  contributor: FraudElementContributors
) => selectFraudElementObservationEntities(state)[`${fraudElementId}_${contributor}`];

export const selectFraudElementsForParty = (state: RootState, partyId?: string) => {
  if (!partyId) return [];
  const ids = state.fraudElements.fraudElementIdsByPartyId[partyId] ?? [];
  return ids
    .map((id) => fraudElementsSelectors.selectById(state, id))
};

export const selectFraudElementCountForParty = (state: RootState, partyId?: string) =>
  partyId
    ? state.fraudElements.fraudElementIdsByPartyId[partyId]?.length ?? 0
    : 0;